import React from 'react';
import lockIcon from '../../../../../../public/assets/images/lockSolid.svg';
interface CitizenshipFeeFieldProps {
  // Single field mode props
  title?: string;
  estimated?: string | number;
  actual?: string | number;
  onActualChange?: (v: string) => void;
  className?: string;
  readyForPayment?: boolean;
  onReadyChange?: (checked: boolean) => void;
  fieldId?: string;
  updatedBy?: string;
  updatedDate?: string;
  showUpdatedInfo?: boolean;
  hasEstimated?: boolean;
  isCommentField?: boolean;
  isDisabled?: boolean;
}

const CitizenshipFeeField: React.FC<CitizenshipFeeFieldProps> = ({
  title = "Filling Fee",
  estimated = "",
  actual = "",
  onActualChange,
  className = "",
  readyForPayment = false,
  onReadyChange,
  fieldId,
  updatedBy,
  updatedDate,
  showUpdatedInfo = false,
  hasEstimated = true,
  isCommentField = false,
  isDisabled = false,
}) => {
  const baseControlClass = "w-full block";
  const disabledClasses = "ds-form__disabled-field bg-gray-100 text-gray-600";




  const handleNumberKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const forbidden = ["-", "e", "E", "ArrowUp", "ArrowDown", "+"];
    // Prevent a second decimal point from being entered
    if (e.key === '.' && (e.target as HTMLInputElement).value.includes('.')) {
      e.preventDefault();
      return;
    }
    if (forbidden.includes(e.key)) e.preventDefault();
  };

  const handleWheel = (e: React.WheelEvent<HTMLInputElement>) => {
    (e.target as HTMLInputElement).blur();
  };

  const handleActualChange = (value: string) => {
    // Allow positive numbers with optional decimal part (up to 2 decimals)
    let sanitizedValue = value.replace(/[^0-9.]/g, '');
    // If more than one decimal point, keep first and remove subsequent
    const parts = sanitizedValue.split('.');
    if (parts.length > 2) {
      const first = parts.shift() || '';
      sanitizedValue = `${first}.${parts.join('')}`;
    }
    // If starts with a decimal point, prefix a 0
    if (sanitizedValue.startsWith('.')) {
      sanitizedValue = `0${sanitizedValue}`;
    }
    // Limit to two decimal places
    if (sanitizedValue.includes('.')) {
      const [intPart, decPart] = sanitizedValue.split('.');
      sanitizedValue = `${intPart}.${(decPart || '').slice(0, 2)}`;
    }
    onActualChange?.(sanitizedValue);
  };

  // Single field mode
  // eslint-disable-next-line prefer-named-capture-group
  const safeBaseId = fieldId ?? title.replace(/\s+/g, '-').toLowerCase();
  // eslint-disable-next-line prefer-named-capture-group
  const estimatedId = `${safeBaseId}-estimated`;
  const actualId = `${safeBaseId}-actual`;

  // Comment field - render TextArea instead of fee fields
  if (isCommentField) {
    const maxLength = 500;
    const textareaId = `textarea-${safeBaseId}`;
    const countId = `textarea-count-${safeBaseId}`;
    const textValue = String(actual ?? '');

    return (
      <div className="w-1/2">
        <div className={`flex items-start gap-4 ${className}`}>
          <div className="shrink-0 min-w-[200px] whitespace-nowrap pt-1">
            <label htmlFor={textareaId} className="label-ds2 font-semibold">{title}</label>
          </div>
          <div className="flex-1">
            <div className="textarea-wrapper w-full relative">
              <textarea
                id={textareaId}
                className="text-area_word-count text-area_global -book !text-base !text-[#0D1C3D] !w-full w-full block h-24 p-2 pr-12 border border-gray-300 rounded bg-gray-100 text-gray-600"
                value={textValue}
                onChange={(e) => onActualChange?.(e.target.value)}
                maxLength={maxLength}
                aria-label={title}
                aria-describedby={countId}
                disabled
              />
              <button type="button" disabled className="absolute right-2 bottom-2">
                <img src={lockIcon} alt="locked" />
              </button>
            </div>
            <div className="character-count mt-2">
              <span
                id={countId}
                aria-live="polite"
                aria-atomic="true"
                className="count-area text-input-helper text-xs text-gray-500"
              >
                {`${maxLength >= textValue.length ? maxLength - textValue.length : 0} of ${maxLength} characters left`}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex items-start gap-4 w-full min-w-0 ${className}`}>
      <div className="shrink-0 min-w-[200px] whitespace-nowrap pt-1">
        <label className="label-ds2 font-semibold">{title}</label>
      </div>

      <div className="flex flex-1 items-start gap-3 min-w-0 flex-col">
        <div className="w-full flex items-center gap-3">
          {/* Estimated - render only if hasEstimated is true */}
          {hasEstimated ? (
            <div className="ds-form flex-1 min-w-0" data-ds-theme="vessel" data-ds-variant="input">
              <label htmlFor={estimatedId} className="mb-1!">Estimated {title.toLowerCase()}</label>
              <div className="input-with-icon">
                <input
                  id={estimatedId}
                  className={`${baseControlClass} ds-form__control ${disabledClasses} pr-12`}
                  disabled
                  type="text"
                  value={Number(estimated) > 0 ? `$${estimated}` : `${estimated}`}
                />
                <button type="button" disabled className="date-input-icon">
                  <img src={lockIcon} alt="locked" />
                </button>
              </div>
            </div>
          ) : (
            <div className="flex-1" />
          )}

          {/* Actual - editable for citizenship */}
          <div className="ds-form flex-1 min-w-0" data-ds-theme="vessel" data-ds-variant="input">
            <label htmlFor={actualId} className="mb-1!">Actual {title.toLowerCase()}</label>
            <div className="input-with-icon">
              <input
                id={actualId}
                className={`${baseControlClass} ds-form__control ${isDisabled ? disabledClasses : ''}`}
                type="number"
                value={String(actual)}
                placeholder="i.e. 635.00"
                onChange={(e) => handleActualChange(e.target.value)}
                onKeyDown={handleNumberKeyDown}
                onWheel={handleWheel}
                min="0"
                step="0.01"
                disabled={isDisabled}
              />
            </div>
          </div>

          {/* Ready for payment checkbox */}
          {readyForPayment !== undefined && (
            <div className="flex items-center gap-2 ml-2">
              <input
                id={fieldId ?? `readyForPayment-${title}`}
                type="checkbox"
                checked={readyForPayment}
                onChange={(e) => onReadyChange?.(e.target.checked)}
                className="w-4 h-4"
                aria-label="Ready for payment"
                disabled={isDisabled || !actual || actual === '' || actual === '0'}
              />
              <label htmlFor={fieldId ?? `readyForPayment-${title}`} className="text-sm">Ready for payment</label>
            </div>
          )}
        </div>

        {/* Updated info displayed below Actual field */}
        {showUpdatedInfo && (updatedBy || updatedDate) && (
          <div className="w-full mt-2 text-xs text-gray-500">
            <div className="flex gap-3">
              <div className="flex-1" />
              <div className="flex-1 flex justify-between gap-4 relative left-24">
                <span>Updated by: {updatedBy ?? '-'}</span>
                <span>Updated date: {updatedDate ? updatedDate.split('-').join('/') : '-'}</span>
              </div>
              <div className="flex-1" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CitizenshipFeeField;
